import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { styled } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { MarketTrendsSwitchGraphV2ExcelDetailParams } from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table, TextCell } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Modal, Textfield } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { toTimeGranularityName, type TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import downloadExcel from "app/components/React/ExcelButton/ExcelClientDownload";
import { TrafficShareWithVisits } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { getSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";
import DurationService from "app/services/DurationService";

import type { RegionalDomainData } from "./types";

const getRegionalDomainsExcelUrl = ({ domain, ...queryParams }: MarketTrendsSwitchGraphV2ExcelDetailParams) => {
    // @ts-expect-error - URLSearchParams accepts a Record<string, string>
    const searchParams = new URLSearchParams(queryParams);
    return `/api/Market/Trends/SwitchGraphV2/Excel/${domain}?${searchParams.toString()}`;
};

const SearchInput = styled(Textfield)`
    width: 100%;

    .input-container {
        border-radius: 0;
        border-right: none;
        border-left: none;
    }
`;

type ColumnDataItem = {
    domain: string;
    trafficShare: number;
};

const columnsConfig: ColumnsType<ColumnDataItem> = [
    {
        dataIndex: "domain",
        get title() {
            return translate("common.domain");
        },
        key: "domain",
        sorter: (left: ColumnDataItem, right: ColumnDataItem) => {
            return (left.domain ?? "").localeCompare(right.domain ?? "");
        },
        render: (value) => <TextCell fontVariant="input2">{value}</TextCell>,
    },
    {
        dataIndex: "trafficShare",
        get title() {
            return translate("common.tables.columns.traffic.share.title");
        },
        key: "trafficShare",
        defaultSortOrder: "descend",
        sorter: (left: ColumnDataItem, right: ColumnDataItem) => {
            return (left.trafficShare ?? 0) - (right.trafficShare ?? 0);
        },
        render: (value, row) => {
            return (
                <TrafficShareWithVisits
                    value={value}
                    row={row}
                    totalVisitsRowProp="visits"
                    visitMinThreshold={0}
                />
            );
        },
    },
];

type RegionalsCountryDomainsModalProps = {
    parentDomainName: string;
    domains: RegionalDomainData[];
    isOpen: boolean;
    onClose: () => void;
    timeGranularity: TimeGranularitySymbol;
};

export const RegionalsCountryDomainsModal = ({
    parentDomainName,
    domains,
    isOpen,
    onClose,
    timeGranularity,
}: RegionalsCountryDomainsModalProps) => {
    const { params } = useCurrentStateAndParams();
    const [searchQuery, setSearchQuery] = useState("");
    const [isExcelPending, setIsExcelPending] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSearchQuery("");
        }
    }, [isOpen, parentDomainName]);

    const trafficShareMap = useMemo(() => {
        if (!domains) {
            return new Map<string, number>();
        }
        const map = new Map<string, number>();
        const totalVisitsSum = domains?.reduce((acc, domain) => acc + domain.AvgMonthVisits, 0);
        domains.forEach((domain) => {
            map.set(domain.Domain, domain.AvgMonthVisits / totalVisitsSum);
        });

        return map;
    }, [domains]);

    const tableData = useMemo(() => {
        if (!domains) {
            return [];
        }

        const searchRegex = new RegExp(searchQuery.toLowerCase(), "i");
        const filteredDomains = searchQuery ? domains.filter((domain) => searchRegex.test(domain.Domain)) : domains;

        return filteredDomains.map((domain) => ({
            domain: domain.Domain,
            visits: domain.AvgMonthVisits,
            trafficShare: trafficShareMap.get(domain.Domain) || 0,
        }));
    }, [domains, trafficShareMap, searchQuery]);

    const handleSearch = (value: string) => {
        setSearchQuery(value);
    };

    const onDownloadExcelClick = async () => {
        const { duration, country, webSource } = params;
        const { from, to } = DurationService.getDurationData(duration).forAPI;
        const selectedCategory = getSelectedCategory();
        const categoryId = selectedCategory?.forApi;

        if (!from || !to || !categoryId) {
            return;
        }

        setIsExcelPending(true);

        try {
            const excelUrl = getRegionalDomainsExcelUrl({
                from,
                to,
                domain: parentDomainName,
                keys: [categoryId],
                country: country,
                timeGranularity: toTimeGranularityName(timeGranularity),
                webSource: webSource,
                includeRegionalDomains: true,
                includeSubdomains: true,
            } satisfies MarketTrendsSwitchGraphV2ExcelDetailParams);

            await downloadExcel(excelUrl);
        } catch (error) {
            Sentry.error("Error downloading regional domains Excel file", error);
        } finally {
            setIsExcelPending(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
        >
            <Modal.CloseButton onClick={onClose} />
            <Modal.Content padding="0">
                <Flex
                    flexDirection="column"
                    height="100%"
                >
                    <Flex
                        flexDirection="column"
                        gap="8px"
                        paddingX="24px"
                        paddingY="20px"
                    >
                        <Text fontVariant="h5_2">{parentDomainName}</Text>
                        <Flex
                            gap="8px"
                            alignItems="center"
                        >
                            <SWReactIcons
                                iconName="regionals-world"
                                size="xs"
                                iconColor={colorsPalettes.carbon[200]}
                            />
                            <Text fontVariant="chip1">
                                {translate("market.players.market.shares.regionals.modal.subtitle", {
                                    domainsCount: domains?.length,
                                })}
                            </Text>
                        </Flex>
                    </Flex>
                    <SearchInput
                        height={48}
                        iconName="search"
                        iconColor={colorsPalettes.carbon[200]}
                        placeholder={translate("common.placeholder.search")}
                        onChange={handleSearch}
                    />
                    <Flex
                        flex="1"
                        overflow="scroll"
                    >
                        <Table
                            sticky
                            data={tableData}
                            columnConfig={columnsConfig}
                            pagination={{ pageSize: domains?.length + 1 }}
                        />
                    </Flex>
                </Flex>
            </Modal.Content>
            <Modal.Footer
                // @ts-expect-error - Modal.Footer in fact accepts padding and other Box props
                padding="8px 16px"
            >
                <IconButton
                    type="primary"
                    iconName="excel"
                    onClick={() => void onDownloadExcelClick()}
                    isLoading={isExcelPending}
                >
                    {translate("widget.common.excel.button.download")}
                </IconButton>
            </Modal.Footer>
        </Modal>
    );
};
